import type { Dayjs } from 'dayjs';

import type {
  CHART_GRAPH_DIRECTION } from '@/entities/chart/model/constants';
import {
  type CHART_CALCULATION,
  type CHART_COLOR_SCALE,
  CHART_COMPARE_TERM_TYPE,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_FREQUENCY,
  CHART_GRAPH,
  type CHART_METRIC,
  type CHART_METRIC_VIEW_TYPE,
  type CHART_PERIOD_RELATIVE,
  type CHART_SHARE_CALCULATION_TYPE,
  CHART_TERM_TYPE,
  type CHART_TERM_UNIT,
  CHART_TYPE,
  ENTIRE_ONLY_FIELDS,
} from '@/entities/chart/model/constants';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { isAllowHistorical } from '@/features/(charts)/chart-renderer/lib/conditions';
import { popup } from '@/stores/pure/PopupStore';
import type { FeedbackSentiment } from '@/types/feedbackConst';
import {
  getEndDateManipulatedDateRange,
  getOpUnitType,
  getTimezoneDayjs,
} from '@/utils/time';

import {
  createComboChartSeries,
  createDefaultCrossAxisSeries,
  createDefaultSingleAxisSeries,
  createFixedTerm,
  createPeriodicTerm,
  getShareCalculationOptions,
  getViewTypeOptions,
  swapFieldAndCrossField,
  updateSeriesMeasure,
  updateSeriesOptions,
} from './chartStateHelpers';

/**
 * State change rule definition
 */
export type StateChangeRule<T = any> = {
  validate?: (currentState: ChartState, payload: T) => boolean;
  sideEffects?: (
    currentState: ChartState,
    payload: T,
    onConfirm?: () => void,
  ) => boolean;
  transform: (currentState: ChartState, payload: T) => Partial<ChartState>;
};

const TEXT_DATASET_FIELDS = new Set<CHART_FIELD>([
  CHART_FIELD.FEEDBACK_KEYWORD,
  CHART_FIELD.FEEDBACK_HASHTAG,
  CHART_FIELD.FEEDBACK_MENTION,
]);

/**
 * Chart Type Change Rules
 */
export const chartTypeChangeRule: StateChangeRule<CHART_TYPE> = {
  transform: (state, type) => {
    if (type === CHART_TYPE.CROSS_AXIS) {
      return {
        type,
        field: {
          type: state.field.type || CHART_FIELD.FEEDBACK_ALL,
          entire:
            typeof state.field.entire === 'undefined'
              ? true
              : state.field.entire,
          candidates: Array.isArray(state.field.candidates)
            ? state.field.candidates
            : [],
        },
        historical: {
          frequency: null,
          value: false,
        },
        series: [createDefaultCrossAxisSeries()],
        selected_field: {
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
        },
        graph: CHART_GRAPH.TABLE,
        target: state.target,
      };
    }

    // Default case (SINGLE_AXIS)
    return {
      type,
      field: {
        type: state.field.type || CHART_FIELD.FEEDBACK_ALL,
        entire:
          typeof state.field.entire === 'undefined' ? true : state.field.entire,
        candidates: Array.isArray(state.field.candidates)
          ? state.field.candidates
          : [],
      },
      series: [createDefaultSingleAxisSeries(state.series[0])],
      graph: CHART_GRAPH.BAR,
      target: state.target,
      selected_field: {
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
        items: state.field.candidates || [],
      },
    };
  },
};

/**
 * Term Type Change Rules
 */
export const termTypeChangeRule: StateChangeRule<CHART_TERM_TYPE> = {
  transform: (state, type) => {
    const isHistorical = isAllowHistorical(state.type, state.graph);
    if (type === CHART_TERM_TYPE.FIXED) {
      const term = createFixedTerm(
        state.target.term.unit,
        state.historical.frequency,
      );

      const newTerm = chartUtils.getChartTermValueByTermType(
        term,
        isHistorical,
      );
      return {
        historical: {
          frequency: isHistorical
            ? chartUtils.getFrequencyByTermUnit(newTerm.unit)
            : null,
          value: isHistorical,
        },
        target: {
          ...state.target,
          term: newTerm,
        },
      };
    } else {
      const term = createPeriodicTerm();
      return {
        historical: {
          frequency: isHistorical
            ? chartUtils.getFrequencyByTermUnit(state.target.term.unit)
            : null,
          value: isHistorical,
        },
        target: {
          ...state.target,
          term: {
            ...term,
            type: type,
          },
        },
      };
    }
  },
};

/**
 * Field Change Rules
 */
export const fieldChangeRule: StateChangeRule<{
  field: CHART_FIELD;
  chartSeriesIndex?: number;
}> = {
  validate: (state, payload) => {
    const { field, chartSeriesIndex } = payload;
    const isColumn = typeof chartSeriesIndex === 'number';

    if (!isColumn) {
      return state.field.type !== field;
    } else {
      return state.series[chartSeriesIndex]?.cross_field?.type !== field;
    }
  },
  transform: (state, payload) => {
    const { field, chartSeriesIndex } = payload;
    const isColumn = typeof chartSeriesIndex === 'number';
    const isEntireOnlyField = ENTIRE_ONLY_FIELDS.has(field);

    if (!isColumn) {
      return {
        field: {
          ...state.field,
          type: field,
          entire: isEntireOnlyField,
          candidates: [],
        },
        selected_field: {
          items: [],
          method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
        },
      };
    } else {
      // cross_field case
      return {
        series: state.series.map((series: ChartSeriesDef, index) =>
          index === chartSeriesIndex
            ? {
                ...series,
                cross_field: {
                  ...series.cross_field!,
                  type: field,
                  entire: isEntireOnlyField,
                  candidates: [],
                },
              }
            : series,
        ),
        selected_field: {
          items: [],
          method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
        },
      };
    }
  },
};

/**
 * Field Item Change Rules
 */
export const fieldItemChangeRule: StateChangeRule<{
  values: StringNumberValue[];
  chartSeriesIndex?: number;
}> = {
  transform: (state, payload) => {
    const { values, chartSeriesIndex } = payload;
    const isColumn = Number.isInteger(chartSeriesIndex);
    const nextValues = Array.from(new Set(values));
    const isTextDatasetField =
      !isColumn && TEXT_DATASET_FIELDS.has(state.field.type);

    if (isTextDatasetField) {
      if (nextValues.length === 0) {
        return {
          field: {
            ...state.field,
            entire: true,
            candidates: [],
          },
          selected_field: {
            items: [],
            method:
              state.graph === CHART_GRAPH.WORD_CLOUD
                ? CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50
                : CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
          },
        };
      }

      return {
        field: {
          ...state.field,
          entire: false,
          candidates: nextValues,
        },
        selected_field: {
          ...state.selected_field,
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: nextValues,
        },
      };
    }

    if (
      state.selected_field.method ===
        CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED &&
      !isColumn
    ) {
      return {
        field: {
          ...state.field,
          entire: false,
          candidates: nextValues,
        },
        selected_field: {
          ...state.selected_field,
          items: state.selected_field.items?.filter((value) =>
            nextValues.includes(value),
          ),
        },
      };
    }
    if (!isColumn) {
      return {
        field: {
          ...state.field,
          entire: false,
          candidates: nextValues,
        },
        selected_field: {
          ...state.selected_field,
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: nextValues,
        },
      };
    }
    // cross_field case
    return {
      series: state.series.map((series: ChartSeriesDef, index) =>
        index === chartSeriesIndex
          ? {
              ...series,
              cross_field: series.cross_field
                ? {
                    ...series.cross_field,
                    entire: ENTIRE_ONLY_FIELDS.has(series.cross_field.type),
                    candidates: values,
                  }
                : undefined,
            }
          : series,
      ),
      selected_field: {
        ...state.selected_field,
      },
    };
  },
};

/**
 * Metric Change Rules
 */
export const metricChangeRule: StateChangeRule<{
  metric: CHART_METRIC;
  chartSeriesIndex: number;
}> = {
  transform: (state, payload) => {
    const { metric, chartSeriesIndex } = payload;

    return {
      series: state.series.map((series: ChartSeriesDef, index) => {
        if (index !== chartSeriesIndex) return series;
        const updated = updateSeriesMeasure(
          series,
          metric,
          series.measure.metric,
        );

        return updated;
      }),
    };
  },
};

/**
 * Calculation Change Rules
 */
export const calculationChangeRule: StateChangeRule<{
  calculation: CHART_CALCULATION;
  chartSeriesIndex: number;
}> = {
  transform: (state, payload) => {
    const { calculation, chartSeriesIndex } = payload;

    return {
      series: state.series.map((series, index) => {
        if (index !== chartSeriesIndex) return series;
        return {
          ...series,
          cross_field: series.cross_field,
          measure: { ...series.measure, calculation },
        };
      }),
    };
  },
};

/**
 * Add Compare Rules
 */
export const addCompareRule: StateChangeRule<void> = {
  transform: (state) => {
    return {
      target: {
        ...state.target,
        is_compare: true,
        compare_term: {
          type: CHART_COMPARE_TERM_TYPE.PREVIOUS,
        },
      },
    };
  },
};

/**
 * Remove Compare Rules
 */
export const removeCompareRule: StateChangeRule<void> = {
  transform: (state) => {
    return {
      target: {
        ...state.target,
        compare_term: undefined,
        is_compare: false,
      },
    };
  },
};

/**
 * Compare Term Type Change Rules
 */
export const compareTermTypeChangeRule: StateChangeRule<CHART_COMPARE_TERM_TYPE> =
  {
    transform: (state, type) => {
      if (type === CHART_COMPARE_TERM_TYPE.FIXED) {
        const prevRange = chartUtils.getCompareTimeRangeByTerm(
          getTimezoneDayjs(),
          state.target.term,
          state.target.compare_term,
        );

        return {
          target: {
            ...state.target,
            compare_term: {
              ...state.target.compare_term,
              type,
              start_date: prevRange[0].toJSON(),
              end_date: prevRange[1].toJSON(),
            },
          },
        };
      }
      return {
        target: {
          ...state.target,
          compare_term: {
            ...state.target.compare_term,
            type,
            start_date: undefined,
            end_date: undefined,
          },
        },
      };
    },
  };

/**
 * Custom Compare Start Change Rules
 */
export const customCompareStartChangeRule: StateChangeRule<Dayjs> = {
  transform: (state, startDate) => {
    const compareTerm = state.target.compare_term;
    if (!compareTerm) return {};

    const opType = getOpUnitType(
      state.historical.frequency || CHART_FREQUENCY.DAY,
    );
    const formattedStartDate = startDate.startOf(opType).toJSON();
    const endDate = chartUtils
      .getCustomCompareTermEndDate(startDate, state.target.term)
      .toJSON();

    return {
      target: {
        ...state.target,
        compare_term: {
          type: compareTerm.type,
          start_date: formattedStartDate,
          end_date: endDate,
        },
      },
    };
  },
};

/**
 * Custom Term Change Rules
 */
export const customTermChangeRule: StateChangeRule<
  [Dayjs | null, Dayjs | null] | null
> = {
  transform: (state, dates) => {
    const opType = getOpUnitType(
      state.historical.frequency || CHART_FREQUENCY.DAY,
    );
    if (state.target.term.type === CHART_TERM_TYPE.FIXED) {
      const newDateRange = getEndDateManipulatedDateRange(
        dates,
        [
          getTimezoneDayjs(state.target.term.start_date),
          getTimezoneDayjs(state.target.term.end_date),
        ],
        opType,
      );

      const diff = newDateRange[1].diff(newDateRange[0], opType);
      const value = diff;

      const term = {
        ...state.target.term,
        start_date: newDateRange[0].toJSON(),
        end_date: newDateRange[1].toJSON(),
        value,
      };

      return {
        target: {
          ...state.target,
          term,
        },
      };
    }

    return {};
  },
};

/**
 * Frequency Change Rules
 */
export const frequencyChangeRule: StateChangeRule<CHART_FREQUENCY> = {
  transform: (state, frequency) => {
    const isHistorical = isAllowHistorical(state.type, state.graph);
    const newTerm = chartUtils.getChartTermValueByTermType(
      {
        ...state.target.term,
        unit: chartUtils.getUnitByFrequency(frequency),
      },
      isHistorical,
    );
    return {
      historical: { value: true, frequency },
      target: {
        ...state.target,
        term: newTerm,
      },
    };
  },
};

/**
 * Term Unit Change Rules
 */
export const termUnitChangeRule: StateChangeRule<CHART_TERM_UNIT> = {
  transform: (state, unit) => {
    const frequency = chartUtils.getFrequencyByTermUnit(unit);
    const maxValue = chartUtils.getMaxRangeByTermUnit(unit);

    if (state.target.term.type === CHART_TERM_TYPE.PERIODIC) {
      const value =
        state.target.term.value > maxValue ? maxValue : state.target.term.value;
      return {
        historical: {
          ...state.historical,
          frequency: state.historical.value ? frequency : null,
        },
        target: {
          ...state.target,
          term: {
            ...state.target.term,
            unit,
            value,
          },
        },
      };
    }
    return {};
  },
};

/**
 * Term Relative Change Rules
 */
export const termRelativeChangeRule: StateChangeRule<CHART_PERIOD_RELATIVE> = {
  transform: (state, relative) => {
    return {
      target: {
        ...state.target,
        term: {
          ...state.target.term,
          relative,
        },
      },
    };
  },
};

/**
 * Term Value Change Rules
 */
export const termValueChangeRule: StateChangeRule<number> = {
  validate: (state, value) => {
    return !isNaN(value);
  },
  transform: (state, value) => {
    return {
      target: {
        ...state.target,
        term: { ...state.target.term, value },
      },
    };
  },
};

/**
 * Feedback Filter Change Rules
 */
export const feedbackFilterChangeRule: StateChangeRule<
  StructuredFilter | undefined
> = {
  transform: (state, filter) => {
    const nextFilter = { ...(filter || {}) };
    return {
      target: {
        ...state.target,
        filter: Object.keys(nextFilter).length > 0 ? nextFilter : undefined,
      },
    };
  },
};

/**
 * WordCloud Dataset Change Rules
 */
export const wordCloudDatasetChangeRule: StateChangeRule<CHART_FIELD> = {
  transform: (state, type) => {
    return {
      field: {
        ...state.field,
        type,
        entire: true,
        candidates: [],
      },
      selected_field: {
        ...state.selected_field,
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50,
        items: [],
      },
    };
  },
};

/**
 * Graph Change Rules
 */
export const graphChangeRule: StateChangeRule<CHART_GRAPH> = {
  validate: (_state, _graph) => {
    // Don't block - let sideEffects handle confirmation
    return true;
  },
  sideEffects: (state, graph, onConfirm) => {
    if (
      state.type === CHART_TYPE.CROSS_AXIS &&
      state.graph === CHART_GRAPH.TABLE &&
      graph !== CHART_GRAPH.TABLE &&
      state.series.length > 1
    ) {
      popup({
        popupTheme: 'neutral',
        type: 'confirm',
        title: 'Are you sure you want to change?',
        message:
          'Changing the chart type will reset your column settings, except for the first data set.',
        onConfirm: () => {
          if (onConfirm) onConfirm();
        },
      });
      return true;
    }
    return false;
  },
  transform: (state, graph) => {
    const isHistorical = isAllowHistorical(state.type, graph);

    // Handle series transformation
    let series: ChartSeriesDef[];
    if (graph === CHART_GRAPH.COMBO_CHART) {
      series = createComboChartSeries(state.series);
    } else if (
      state.type === CHART_TYPE.CROSS_AXIS &&
      state.graph === CHART_GRAPH.TABLE &&
      state.series.length > 1
    ) {
      // Slice to first series when changing from table
      series = state.series.slice(0, 1);
    } else {
      series = [state.series[0]];
    }

    const newTerm = chartUtils.getChartTermValueByTermType(
      state.target.term,
      isHistorical,
    );
    const hasUserDefinedFieldItems =
      state.selected_field.method ===
        CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED &&
      (state.selected_field.items?.length ?? 0) > 0;

    return {
      graph,
      selected_field:
        graph === CHART_GRAPH.WORD_CLOUD
          ? hasUserDefinedFieldItems
            ? state.selected_field
            : {
                method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50,
                items: [],
              }
          : state.selected_field,
      historical: {
        frequency: isHistorical
          ? chartUtils.getFrequencyByTermUnit(state.target.term.unit)
          : null,
        value: isHistorical,
      },
      series,
      target:
        graph === CHART_GRAPH.COMBO_CHART
          ? {
              ...state.target,
              is_compare: false,
              compare_term: undefined,
              term: newTerm,
            }
          : {
              ...state.target,
              term: newTerm,
            },
    };
  },
};

/**
 * Graph Direction Change Rules
 */
export const graphDirectionChangeRule: StateChangeRule<CHART_GRAPH_DIRECTION> =
  {
    transform: (state, direction) => {
      return {
        graph_direction: direction,
      };
    },
  };

/**
 * View Type Change Rules
 */
export const viewTypeChangeRule: StateChangeRule<{
  chartSeriesIndex: number;
  viewType: CHART_METRIC_VIEW_TYPE;
}> = {
  transform: (state, payload) => {
    const { chartSeriesIndex, viewType } = payload;

    return {
      series: state.series.map((series, index) => {
        if (index !== chartSeriesIndex) return series;

        const options = getViewTypeOptions(viewType, series.options);

        return {
          ...series,
          options,
        };
      }),
    };
  },
};

/**
 * Scale Option Change Rules
 */
export const scaleOptionChangeRule: StateChangeRule<{
  chartSeriesIndex: number;
  scale?: ChartScale;
}> = {
  transform: (state, payload) => {
    const { chartSeriesIndex, scale } = payload;

    return {
      series: state.series.map((series, index) => {
        if (index !== chartSeriesIndex) return series;
        const updated = updateSeriesOptions(series, {
          scale_min: scale?.min,
          scale_max: scale?.max,
        });
        return updated;
      }),
    };
  },
};

/**
 * Color Scale Change Rules
 */
export const colorScaleChangeRule: StateChangeRule<{
  chartSeriesIndex: number;
  colorScale?: CHART_COLOR_SCALE;
}> = {
  transform: (state, payload) => {
    const { chartSeriesIndex, colorScale } = payload;

    return {
      series: state.series.map((series, index) => {
        if (index !== chartSeriesIndex) return series;
        const updated = updateSeriesOptions(series, {
          color_scale: colorScale,
        });
        return updated;
      }),
    };
  },
};

/**
 * Historical Change Rules
 */
export const historicalChangeRule: StateChangeRule<boolean> = {
  transform: (state, value) => {
    return {
      historical: {
        ...state.historical,
        value,
        frequency: value
          ? chartUtils.getFrequencyByTermUnit(state.target.term.unit)
          : null,
      },
    };
  },
};

/**
 * Share Calculation Type Change Rules
 */
export const shareCalculationTypeChangeRule: StateChangeRule<{
  chartSeriesIndex: number;
  shareCalculationType: CHART_SHARE_CALCULATION_TYPE;
}> = {
  transform: (state, payload) => {
    const { chartSeriesIndex, shareCalculationType } = payload;

    return {
      series: state.series.map((series, index) => {
        if (index !== chartSeriesIndex) return series;
        const updated = updateSeriesOptions(
          series,
          getShareCalculationOptions(shareCalculationType, series.options),
        );
        return updated;
      }),
    };
  },
};

/**
 * Share Cell Calculation Segment Change Rules
 */
export const shareCellCalculationSegmentChangeRule: StateChangeRule<{
  chartSeriesIndex: number;
  segment: FeedbackSentiment;
}> = {
  transform: (state, payload) => {
    const { chartSeriesIndex, segment } = payload;

    return {
      series: state.series.map((series, index) => {
        if (index !== chartSeriesIndex) return series;
        const updated = updateSeriesOptions(series, {
          share_cell_calculation_segment: segment,
        });
        return updated;
      }),
    };
  },
};

/**
 * Dataset Switch Rules
 */
export const datasetSwitchRule: StateChangeRule<void> = {
  validate: (state) => {
    return state.series.length <= 1;
  },
  transform: (state) => {
    const { newField, newSeries } = swapFieldAndCrossField(
      state.field,
      state.series,
    );
    return {
      field: newField,
      series: newSeries,
    };
  },
};

/**
 * Show Row Total Change Rules
 */
export const showRowTotalChangeRule: StateChangeRule<{
  value: boolean;
  chartSeriesIndex: number;
}> = {
  transform: (state, payload) => {
    const { value, chartSeriesIndex } = payload;

    return {
      series: state.series.map((seriesItem: ChartSeriesDef, index: number) => {
        if (index !== chartSeriesIndex) return seriesItem;
        return {
          ...seriesItem,
          options: {
            ...seriesItem.options,
            show_row_total: value,
          },
        };
      }),
    };
  },
};
